import type { Awareness } from 'y-protocols/awareness'

/**
 * Presence — multiplayer awareness shapes for the diagram editor.
 *
 * The awareness channel (provided by the y-websocket transport via
 * SyncProvider.awareness) carries a small, ephemeral, per-client state object.
 * For diagram presence that state is a {@link PresenceLocalState}: a stable
 * identity (name + color derived from the Yjs clientID) plus the live cursor
 * position (in flow coordinates) and the set of currently selected element ids.
 *
 * These helpers are PURE so the presence-state shape and the awareness → peer
 * reduction can be unit-tested without a live provider or a DOM.
 */

/** A flow-coordinate point used for remote cursor rendering. */
export interface PresencePoint {
  x: number
  y: number
}

/** The local client's published presence state (one object per peer). */
export interface PresenceLocalState {
  /** Human-readable label shown on the cursor + avatar. */
  name: string
  /** Stable CSS color for this peer's cursor/avatar/selection highlight. */
  color: string
  /** Live cursor position in flow coordinates (omitted when off-canvas). */
  cursor?: PresencePoint
  /** Ids of the elements this peer currently has selected. */
  selection?: string[]
}

/** A remote peer = its presence state tagged with the Yjs client id. */
export interface PresencePeer extends PresenceLocalState {
  clientId: number
}

/**
 * Deterministic, evenly-spread hue for a Yjs client id → a stable HSL color so
 * the same peer keeps the same color for the life of the session without any
 * coordination.
 */
export function presenceColorFor(clientId: number): string {
  // Golden-angle stepping keeps successive ids visually distinct.
  const hue = Math.abs(Math.round(clientId * 137.508)) % 360
  return `hsl(${hue} 70% 50%)`
}

/** Deterministic, friendly display name for a Yjs client id. */
export function presenceNameFor(clientId: number): string {
  return `User ${clientId % 1000}`
}

/**
 * Build the local client's presence state: a stable identity (name + color from
 * the client id) merged with the live cursor/selection. Pure → unit-testable.
 */
export function makeLocalPresence(
  clientId: number,
  live: { cursor?: PresencePoint; selection?: string[] } = {},
): PresenceLocalState {
  const state: PresenceLocalState = {
    name: presenceNameFor(clientId),
    color: presenceColorFor(clientId),
  }
  if (live.cursor) state.cursor = live.cursor
  if (live.selection) state.selection = live.selection
  return state
}

/**
 * Reduce an awareness channel into a renderable list of remote peers, excluding
 * the local client and any state missing the required name/color identity.
 */
export function presenceFromAwareness(
  awareness: Awareness,
  localClientId: number,
): PresencePeer[] {
  const peers: PresencePeer[] = []
  awareness.getStates().forEach((state, clientId) => {
    if (clientId === localClientId) return
    const s = state as Partial<PresenceLocalState>
    if (typeof s.name !== 'string' || typeof s.color !== 'string') return
    const peer: PresencePeer = { clientId, name: s.name, color: s.color }
    if (s.cursor && typeof s.cursor.x === 'number' && typeof s.cursor.y === 'number') {
      peer.cursor = { x: s.cursor.x, y: s.cursor.y }
    }
    if (Array.isArray(s.selection)) peer.selection = s.selection
    peers.push(peer)
  })
  return peers
}
